import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DomainContact } from '../types/models/DomainContact';
import type { ApiResponseDomainContact } from '../types/api/ApiResponseDomainName';

interface UseDomainContactReturn {
  /** The registrant contact, or `null` when the domain has none set yet (a valid success). */
  contact: DomainContact | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches a domain's registrant (WHOIS owner) contact from its own sub-resource
 * (`GET /datacenters/domain-names/{id}/contact`). The contact is NOT part of the domain object, so
 * detail-page tabs that show it fetch it here; pair with the parent Tabs' `mountOnEnter` so the
 * request fires only when the Contacts tab is first opened.
 */
export const useDomainContact = (id: number): UseDomainContactReturn => {
  const [contact, setContact] = useState<DomainContact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchContact = useCallback(async () => {
    if (id <= 0) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/domain-names/${id}/contact`);
      // JSend: the backend maps controller exceptions to status:'error' at HTTP 200, so the
      // authoritative check is body.status, not response.ok. A null contact is a valid success
      // (the domain simply has no registrant contact yet) — do not treat it as an error.
      const body = (await response.json().catch(() => null)) as ApiResponseDomainContact | null;

      if (body?.status === 'success') {
        setContact(body.data?.contact ?? null);
      } else {
        setError(body?.message ?? 'Failed to load the registrant contact');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading the registrant contact');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchContact();
    }
  }, [auth.isAuthenticated, fetchContact]);

  return { contact, loading, error, refetch: fetchContact };
};
